/**
 * Dashboards: several charts on one page, narrowed together, with no React in it.
 *
 * ## What a dashboard is here
 *
 * A name and a list of questions, each a copy of a chart's form (`state`), plus
 * the filters shared across them. It holds no answer, no record and no tag. Two
 * people who open the same dashboard ask the same questions and are answered
 * for what each of them may open, so each sees their own slice and neither is
 * told the slice is a slice.
 *
 * It is kept in this browser until the Report type exists (LO-217), and says
 * so on screen, for the same reason saved reports do.
 *
 * ## A shared filter applies to every chart it can, and says where it cannot
 *
 * A filter on `status` cannot narrow a chart of people, and a date range cannot
 * narrow a chart that is not drawn over a date. Quietly skipping those would
 * leave a dashboard that looks narrowed and is not. So every chart carries a
 * note for each shared filter that did not apply to it, with the reason.
 */
import {
  buildSpec,
  fieldOf,
  filterFields,
  fieldsOf,
  humanise,
  kindOf,
  opsFor,
  parseValue,
  typeOf,
} from "./spec.js";

const KEY = "insights.dashboards.v1";
export const MAX_ITEMS = 12;

function store(storage) {
  try {
    return storage || globalThis.localStorage || null;
  } catch {
    return null;
  }
}

const valid = (d) => d && d.id && d.name && Array.isArray(d.items);

export function loadDashboards(owner, storage) {
  const s = store(storage);
  if (!s) return [];
  try {
    const list = JSON.parse(s.getItem(`${KEY}:${owner}`) || "[]");
    return Array.isArray(list) ? list.filter(valid).map(normalise) : [];
  } catch {
    return [];
  }
}

export function saveDashboards(owner, list, storage) {
  const s = store(storage);
  if (!s) return false;
  try {
    s.setItem(`${KEY}:${owner}`, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

function normalise(d) {
  return {
    ...d,
    filters: Array.isArray(d.filters) ? d.filters : [],
    range: { from: d.range?.from || "", to: d.range?.to || "" },
  };
}

const uid = (now) => `${now().getTime().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function newDashboard(name, now = () => new Date()) {
  return normalise({ id: `d${uid(now)}`, name: String(name).trim() || "Untitled", items: [], createdAt: now().toISOString() });
}

/** The dashboard with one more chart, or the same one when it is full. */
export function addItem(dashboard, title, state, { sourceId = "", now = () => new Date() } = {}) {
  if (dashboard.items.length >= MAX_ITEMS) return dashboard;
  const item = {
    id: `i${uid(now)}`,
    title: String(title).trim() || "Untitled",
    state: JSON.parse(JSON.stringify(state)),
    ...(sourceId ? { sourceId } : {}),
  };
  return { ...dashboard, items: [...dashboard.items, item] };
}

export const removeItem = (dashboard, id) => ({ ...dashboard, items: dashboard.items.filter((i) => i.id !== id) });

/**
 * The fields a shared filter may name: those of any chart on the dashboard that
 * can be filtered on, once per name and kind. A name that means a text in one
 * type and a number in another is two entries, because they take different
 * operators and one filter cannot be both.
 */
export function sharedFields(dashboard, schema) {
  const seen = new Map();
  for (const item of dashboard.items) {
    for (const f of filterFields(fieldsOf(schema, item.state.type))) {
      const id = `${f.name}:${kindOf(f)}`;
      if (!seen.has(id)) seen.set(id, f);
    }
  }
  return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
}

const dayAfter = (iso) => {
  const t = Date.parse(`${iso}T00:00:00Z`);
  return Number.isNaN(t) ? null : new Date(t + 86_400_000).toISOString().slice(0, 10);
};

/**
 * One chart's form with the dashboard's shared filters added, and a note for
 * each that could not be.
 *
 * The date range is applied to the date the chart is drawn over: the first of
 * its groupings that is a time. A chart with no such grouping has no date of its
 * own to narrow, and says so.
 */
export function applyShared(item, schema, shared) {
  const state = { ...item.state, filters: [...item.state.filters] };
  const notes = [];
  const type = typeOf(schema, state.type)?.type || state.type;

  for (const f of shared.filters || []) {
    if (!f.field) continue;
    const field = fieldOf(schema, state.type, f.field);
    if (!field) {
      notes.push(`${humanise(f.field)} does not narrow this chart: ${type} has no such field.`);
      continue;
    }
    if (!filterFields([field]).length) {
      notes.push(`${humanise(f.field)} does not narrow this chart: it can only be counted here.`);
      continue;
    }
    if (!opsFor(field).includes(f.op) || !parseValue(field, f.op, f.value).ok) {
      notes.push(`${humanise(f.field)} does not narrow this chart: ${type} reads it differently.`);
      continue;
    }
    state.filters.push({ field: f.field, op: f.op, value: f.value ?? "" });
  }

  const { from, to } = shared.range || {};
  if (from || to) {
    const dated = state.dimensions.find((d) => {
      const field = fieldOf(schema, state.type, d.field);
      return field && kindOf(field) === "time";
    });
    if (!dated) {
      notes.push("The date range does not narrow this chart: it is not drawn over a date.");
    } else {
      if (from) state.filters.push({ field: dated.field, op: "gte", value: from });
      const end = to ? dayAfter(to) : null;
      if (end) state.filters.push({ field: dated.field, op: "lt", value: end });
    }
  }
  return { state, notes };
}

/** What a dashboard's shared filters say, in a line, for the page and for a test. */
export function describeShared(shared) {
  const parts = [];
  const { from, to } = shared.range || {};
  if (from || to) parts.push(`dates ${[from && `from ${from}`, to && `to ${to}`].filter(Boolean).join(" ")}`);
  for (const f of shared.filters || []) {
    if (!f.field) continue;
    const value = f.value !== "" && f.value != null ? ` ${f.value}` : "";
    parts.push(`${humanise(f.field).toLowerCase()} ${f.op.replace(/_/g, " ")}${value}`);
  }
  return parts.length ? parts.join(", ") : "none";
}

/** The spec a dashboard's chart is asked, or null while its form is unfinished. */
export function itemSpec(item, schema, shared) {
  const { state } = applyShared(item, schema, shared);
  const spec = buildSpec(state, schema);
  return spec.measures.length ? spec : null;
}
